/**
 * User domain types - single source of truth for user enums.
 * These are shared between backend and frontends.
 */
import type {
  UsersRole as PrismaUsersRole,
  DefaultProfilePicture as PrismaDefaultProfilePicture,
  LemonCertifiedRequestStatus as PrismaLemonCertifiedRequestStatus,
  AdminPermissionLabel as PrismaAdminPermissionLabel,
  AdminPermission as PrismaAdminPermission,
  SupportedLanguagesEnum,
} from "../../service/prisma/client.ts";
import type prisma from "../../service/prisma/index.ts";

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CONTEXT USER (read-flip vague 3.1) — the authenticated principal
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type PgUserRow = Awaited<ReturnType<typeof prisma.user.findUniqueOrThrow>>;

/**
 * READ-FLIPPED (vague 3.1): the auth middleware now loads the principal from
 * PG, so `c.var.user` is the FLAT PG row — no `meta`/`metrics` composites
 * (inlined to flat columns: `createdAt`, `lastLogin`, `followers`, …), and the
 * Mongo `main_specialty`/`medical_specialties` are `mainSpecialty`/
 * `medicalSpecialties`. The PG-only `internal` composite is flattened to
 * `internalOrganisation`.
 *
 * The PG row's enum scalars (`language`, `roles`) are typed via the PG client,
 * which the frontends cannot name → TS2883 declaration-emit portability error
 * the moment a handler's inferred response leaks the principal. Re-express
 * exactly those via the Mongo client enums (the wire's lingua franca, identical
 * string unions, structural + lossless), mirroring `WireRoomRow`.
 *
 * `adminPermissions` is also re-expressed: the JSONB column stores the Mongo
 * `@map`'d DB value `"sensitive data"` (space), but the whole app (guards,
 * admin/dashboard frontends) keys on the Prisma enum member `"sensitive_data"`
 * (Prisma can't translate a value nested inside JSONB the way it does an enum
 * column). `toContextUser` normalises it at the single principal-load boundary
 * so the served wire is unchanged.
 */
export type ContextUser = Omit<
  PgUserRow,
  "language" | "roles" | "adminPermissions"
> & {
  language: SupportedLanguagesEnum;
  roles: PrismaUsersRole[];
  adminPermissions: {
    label: PrismaAdminPermissionLabel;
    permissions: PrismaAdminPermission[];
  }[];
  /**
   * The single org used wherever a request needs ONE owning org (content /
   * podcast / crawling creation). Defaults to the first of `organisations`; the
   * impersonation middleware overrides it from the `X-Impersonate-Organisation`
   * header (a juisciAdmin "view as", or a multi-org admin_org picking one of
   * their own orgs). `null` only when the principal belongs to no org (e.g. a
   * super_admin/juisci that hasn't impersonated) — create sites then 400.
   * When an org IS picked the middleware also narrows `organisations` to that
   * one org (view-as), so the whole request scopes to it; with no pick,
   * `organisations` stays the FULL list and lists/search/room-access span every
   * org the user belongs to.
   */
  activeOrganisation: string | null;
};

/**
 * Normalise a freshly-read PG user row into the principal `ContextUser`. Pure;
 * the only runtime change is mapping each `adminPermissions[].label` from the
 * stored `@map` DB value to the canonical Prisma enum member (only
 * `"sensitive data"` → `"sensitive_data"` actually differs). Apply at every
 * principal-load site (the auth middlewares) so `c.var.user.adminPermissions`
 * matches `AdminPermissionLabel` everywhere downstream.
 */
export function toContextUser(row: PgUserRow): ContextUser {
  return {
    ...row,
    activeOrganisation: row.organisations.at(0) ?? null,
    adminPermissions: row.adminPermissions.map((p) => ({
      label: p.label === "sensitive data" ? "sensitive_data" : p.label,
      permissions: p.permissions,
    })),
  };
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// USERS ROLE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Re-exported from @prisma/client as type alias */
export type UsersRole = PrismaUsersRole;

/** Exhaustive enum-like object - TS error if Prisma enum changes */
export const UsersRole: { [K in UsersRole]: K } = {
  admin_org: "admin_org",
  super_admin: "super_admin",
  juisci: "juisci",
};

export const USERS_ROLES = Object.values(UsersRole);

/**
 * Every role that grants some administrative reach: `admin_org` over its own
 * organisations, `juisci` and `super_admin` across all of them.
 *
 * Here rather than in `router/admin/guard.ts` because it is read well outside
 * the admin router now: the Firebase password rotation spares these accounts,
 * and the trusted-device exemption refuses to cover them
 * (`service/trusted-device.ts`). A role vocabulary that lives inside one router
 * is a role vocabulary its other readers import backwards.
 */
export const adminRoleSet = new Set([
  UsersRole.admin_org,
  UsersRole.juisci,
  UsersRole.super_admin,
]);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// DEFAULT PROFILE PICTURE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Re-exported from @prisma/client as type alias */
export type DefaultProfilePicture = PrismaDefaultProfilePicture;

/**
 * Enum-like const with Title case keys mapping to Prisma's lowercase values.
 * Exhaustive - TS error if Prisma enum changes.
 */
export const DefaultProfilePicture = {
  Apple: "apple",
  Avocado: "avocado",
  Pineapple: "pineapple",
  Pear: "pear",
  Lemon: "lemon",
  Banana: "banana",
} as const satisfies Record<string, DefaultProfilePicture>;

export const DEFAULT_PROFILE_PICTURES: DefaultProfilePicture[] = [
  "apple",
  "avocado",
  "pineapple",
  "pear",
  "lemon",
  "banana",
];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// LEMON CERTIFIED REQUEST STATUS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Re-exported from @prisma/client as type alias */
export type LemonCertifiedRequestStatus = PrismaLemonCertifiedRequestStatus;

/** Exhaustive enum-like object - TS error if Prisma enum changes */
export const LemonCertifiedRequestStatus: {
  [K in LemonCertifiedRequestStatus]: K;
} = { Waiting: "Waiting", Rejected: "Rejected", Approved: "Approved" };

export const LEMON_CERTIFIED_REQUEST_STATUSES = Object.values(
  LemonCertifiedRequestStatus,
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ADMIN PERMISSION LABEL
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Re-exported from @prisma/client as type alias */
export type AdminPermissionLabel = PrismaAdminPermissionLabel;

/** Exhaustive enum-like object - TS error if Prisma enum changes */
export const AdminPermissionLabel = {
  Content: "content",
  Referential: "referential",
  Sources: "sources",
  SensitiveData: "sensitive_data",
  // Validate and send the client change notices
  // (docs/client-change-notifications.md, REQ-12), with `publish`.
  ClientChanges: "client_changes",
} as const satisfies Record<string, AdminPermissionLabel>;

export const ADMIN_PERMISSION_LABELS = Object.values(AdminPermissionLabel);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ADMIN PERMISSION
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Re-exported from @prisma/client as type alias */
export type AdminPermission = PrismaAdminPermission;

/** Exhaustive enum-like object - TS error if Prisma enum changes */
export const AdminPermission = {
  Read: "read",
  Write: "write",
  Publish: "publish",
  Delete: "delete",
} as const satisfies Record<string, AdminPermission>;

export const ADMIN_PERMISSIONS = Object.values(AdminPermission);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ADMIN PERMISSION PREDICATES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// Roles model:
//   super_admin ≡ juisci + every adminPermission + super_admin-only powers
//                 (capability shortcut, ignores adminPermissions)
//   juisci      ≡ admin_org on every org, .read on every label implicit,
//                 plus the adminPermissions matrix for write/publish/delete
//   admin_org   = scoped to user.organisations[], plus the adminPermissions matrix
//
// hasCapability and hasOrgAccess are the canonical checks for action and
// resource access. Sensitive admin-management helpers also require cross-org
// admin scope because they operate on admin accounts rather than ordinary
// org-scoped resources.

type UserWithPermissions = {
  roles: UsersRole[];
  adminPermissions: {
    label: AdminPermissionLabel;
    permissions: AdminPermission[];
  }[];
};

type UserWithOrgAccess = { roles: UsersRole[]; organisations: string[] };

export function hasRole(
  user: { roles: UsersRole[] },
  role: UsersRole,
): boolean {
  return user.roles.includes(role);
}

export function hasSuperAdminRole(user: { roles: UsersRole[] }): boolean {
  return hasRole(user, UsersRole.super_admin);
}

export function hasJuisciRole(user: { roles: UsersRole[] }): boolean {
  return hasRole(user, UsersRole.juisci);
}

export function hasAdminOrgRole(user: { roles: UsersRole[] }): boolean {
  return hasRole(user, UsersRole.admin_org);
}

/**
 * Does this user hold (label, permission)?
 *   super_admin → always true (the role already implies every permission)
 *   juisci → .read on every label implicit; write/publish/delete must have
 *            the matching adminPermissions entry
 *   admin_org → must have the matching adminPermissions entry
 *   any other role (or none) → always false
 */
export function hasCapability(
  user: UserWithPermissions,
  label: AdminPermissionLabel,
  permission: AdminPermission,
): boolean {
  if (hasSuperAdminRole(user)) return true;
  if (hasJuisciRole(user) && permission === AdminPermission.Read) return true;
  if (!hasJuisciRole(user) && !hasAdminOrgRole(user)) return false;
  if (!juisciAdmin(user) && !canAssignAdminPermissionLabelToTarget(user, label))
    return false;
  return user.adminPermissions.some(
    (p) => p.label === label && p.permissions.includes(permission),
  );
}

/**
 * Does this user have access to this organisation?
 *   super_admin/juisci → every org
 *   admin_org → only orgs in user.organisations[]
 *   any other role → no org access
 */
export function hasOrgAccess(
  user: UserWithOrgAccess,
  organisationId: string,
): boolean {
  if (hasSuperAdminRole(user)) return true;
  if (hasJuisciRole(user)) return true;
  if (hasAdminOrgRole(user)) return user.organisations.includes(organisationId);
  return false;
}

/**
 * Is this user a cross-org admin? True for super_admin and juisci — they can
 * operate without an org filter (multi-org list views, global content, etc.).
 * admin_org is always scoped to its specific orgs and returns false here.
 */
export function juisciAdmin(user: { roles: UsersRole[] }): boolean {
  if (hasSuperAdminRole(user)) return true;
  return hasJuisciRole(user);
}

export function canAssignAdminPermissionLabelToTarget(
  target: { roles: UsersRole[] },
  label: AdminPermissionLabel,
): boolean {
  if (juisciAdmin(target)) return true;
  if (label === AdminPermissionLabel.Referential) return false;
  if (label === AdminPermissionLabel.Sources) return false;
  if (label === AdminPermissionLabel.ClientChanges) return false;
  return true;
}

/**
 * Capability projection for one label as four named booleans. Pure: takes a
 * possibly-null user (loading/unauthenticated state) and returns a frozen
 * matrix. Wrap in a per-app `usePermissions(label)` hook that supplies the
 * current user.
 */
export function permissionMatrix(
  user: UserWithPermissions | null,
  label: AdminPermissionLabel,
): {
  canRead: boolean;
  canWrite: boolean;
  canPublish: boolean;
  canDelete: boolean;
} {
  if (user === null)
    return {
      canRead: false,
      canWrite: false,
      canPublish: false,
      canDelete: false,
    };
  return {
    canRead: hasCapability(user, label, AdminPermission.Read),
    canWrite: hasCapability(user, label, AdminPermission.Write),
    canPublish: hasCapability(user, label, AdminPermission.Publish),
    canDelete: hasCapability(user, label, AdminPermission.Delete),
  };
}
